/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { isEqual } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import type {
  AnimationState,
  ApplicationConfig,
  ApplicationConfigurationState,
  FeatureHoverState,
  FeatureSelectionState,
  FeaturesState,
  PluginManager,
  PluginVisibility,
  ScopeEventMap,
  ScopeName,
  ScopeStateMap,
  TimeState,
} from '@opengeoweb/plugin-interface';
import { GEOWEB_AUTH_ISSUER } from '@opengeoweb/plugin-interface';
import type { ConfigType } from '@opengeoweb/shared';
import type { CoreAppStore, commonTypes, layerTypes } from '@opengeoweb/store';
import {
  commonSelectors,
  genericSelectors,
  mapSelectors,
} from '@opengeoweb/store';
import type { IsEventRoundTrip } from '../../hooks';
import { useSendOnReduxChange, useSetScopeStateOnChange } from '../../hooks';

interface UseReduxToPluginOptions {
  pluginBridge: PluginManager;
  sourceId: string;
  hasScope: (scope: ScopeName) => boolean;
  applicationConfig?: ConfigType;
  /** Visibility of this plugin's overlay window, undefined for view plugins. */
  overlayVisibility?: PluginVisibility;
}

interface AuthSource {
  isLoggedIn: boolean;
  credentials: commonTypes.Credentials | undefined;
}

const hasLayerIdentity = (
  layer: layerTypes.ReduxLayer,
): layer is layerTypes.ReduxLayer & {
  id: string;
  geojson: GeoJSON.FeatureCollection;
} => !!layer.id && !!layer.geojson;

const toApplicationConfig = (config: ConfigType): ApplicationConfig => ({
  frontend: {
    global: {
      sentryEnabled: config.GW_FEATURE_INITIALIZE_SENTRY,
      theme: config.GW_DEFAULT_THEME,
      language: config.GW_LANGUAGE,
      initialPresetsFilename: config.GW_INITIAL_PRESETS_FILENAME,
    },
  },
  backend: {
    trajks: {
      baseUrl: config.GW_KNMI_PUBLISHER_BASE_URL,
      workspaceId: config.GW_TRAJKS_WORKSPACE_ID,
    },
    warnings: {
      baseUrl: config.GW_DRAWINGS_BASE_URL,
    },
  },
});

export const useReduxToPlugin = ({
  pluginBridge,
  sourceId,
  hasScope,
  applicationConfig,
  overlayVisibility,
}: UseReduxToPluginOptions): void => {
  const timeValue = useSelector((store: CoreAppStore) =>
    genericSelectors.getTime(
      genericSelectors.getSynchronizationGroupStore(store),
      sourceId,
    ),
  );
  const animationStartTime = useSelector((store: CoreAppStore) =>
    mapSelectors.getAnimationStartTime(store, sourceId),
  );
  const animationEndTime = useSelector((store: CoreAppStore) =>
    mapSelectors.getAnimationEndTime(store, sourceId),
  );
  const animationState = useMemo(
    () => ({ start: animationStartTime, end: animationEndTime }),
    [animationStartTime, animationEndTime],
  );
  const pluginFeatureLayers = useSelector((store: CoreAppStore) =>
    genericSelectors.selectFeatureSyncLayersForLinkedMaps(store, sourceId),
  );
  const featureLayerSelections = useSelector((store: CoreAppStore) =>
    genericSelectors.selectFeatureLayerSelectionsForLinkedMaps(store, sourceId),
  );
  const featureLayerHover = useSelector((store: CoreAppStore) =>
    genericSelectors.selectFeatureLayerHoverForLinkedMaps(store, sourceId),
  );
  const isLoggedIn = useSelector((store: CoreAppStore) =>
    commonSelectors.getIsLoggedIn(store),
  );
  const credentials = useSelector((store: CoreAppStore) =>
    commonSelectors.getCredentials(store),
  );
  const authSource: AuthSource = useMemo(
    () => ({ isLoggedIn, credentials }),
    [isLoggedIn, credentials],
  );

  const toTimeEvent = useCallback(
    (time: typeof timeValue) =>
      time?.currentTime
        ? {
            type: 'setTime' as const,
            time: time.currentTime.replace(/\.\d{3}Z$/, 'Z'),
          }
        : null,
    [],
  );

  const toAnimationEvent = useCallback(
    (anim: { start: string | undefined; end: string | undefined }) =>
      anim.start !== undefined && anim.end !== undefined
        ? {
            type: 'setAnimationTimeSpan' as const,
            startTime: anim.start,
            endTime: anim.end,
          }
        : null,
    [],
  );

  const toFeatureSelectionState = useCallback(
    (
      featureLayerSelections: {
        id: string;
        selectedFeatureIds: string[];
      }[],
    ): FeatureSelectionState | null => ({
      featureLayers: featureLayerSelections,
    }),
    [],
  );

  const toFeatureHoverEvents = useCallback(
    (
      hoveredFeatureLayers: FeatureHoverState['featureLayers'],
    ): ScopeEventMap['featureHover'][] =>
      hoveredFeatureLayers.map((layer) => ({
        type: 'setHoveredFeatures',
        featureLayerId: layer.id,
        hoveredFeatureIds: layer.hoveredFeatureIds,
      })),
    [],
  );

  const toFeaturesEvents = useCallback(
    (layers: typeof pluginFeatureLayers): ScopeEventMap['features'][] => {
      const bridgeState = pluginBridge.getState('features') as
        | FeaturesState
        | undefined;
      const bridgeLayers = new Map(
        (bridgeState?.featureLayers ?? []).map((layer) => [layer.id, layer]),
      );
      const syncedLayers = new Map(
        layers
          .filter(hasLayerIdentity)
          .map((layer) => [
            layer.featureSync?.featureLayerKey ?? layer.id,
            layer,
          ]),
      );
      const events: ScopeEventMap['features'][] = [];

      bridgeLayers.forEach((_layer, layerId) => {
        if (!syncedLayers.has(layerId)) {
          events.push({
            type: 'removeLayer',
            layerId,
          });
        }
      });

      syncedLayers.forEach((layer, layerId) => {
        const bridgeLayer = bridgeLayers.get(layerId);
        if (!bridgeLayer) {
          events.push({
            type: 'addLayer',
            layer: {
              id: layerId,
              title: layerId,
              featureCollection: layer.geojson,
            },
          });
          return;
        }

        if (!isEqual(bridgeLayer.featureCollection, layer.geojson)) {
          events.push({
            type: 'setLayerFeatures',
            layerId,
            featureCollection: layer.geojson,
          });
        }
      });

      return events;
    },
    [pluginBridge],
  );

  const toApplicationConfigurationState = useCallback(
    (config: ConfigType | undefined): ApplicationConfigurationState | null =>
      config && Object.keys(config).length > 0
        ? { geowebConfig: toApplicationConfig(config) }
        : null,
    [],
  );

  const toUserScopeState = useCallback(
    ({ isLoggedIn, credentials }: AuthSource): ScopeStateMap['user'] | null => {
      if (!credentials) {
        return {
          isLoggedIn: false,
          user: null,
          hasConnectionIssue: false,
        };
      }
      return {
        isLoggedIn,
        user: isLoggedIn
          ? {
              username: credentials.username,
              // Currently not used in the opengeoweb
              firstName: '',
              lastName: '',
              rolesPerIssuer: [
                {
                  issuer: GEOWEB_AUTH_ISSUER,
                  roles: (credentials.roles ?? []).map((role) => role.name),
                },
              ],
            }
          : null,
        hasConnectionIssue: credentials.has_connection_issue ?? false,
      };
    },
    [],
  );

  const toAuthenticationScopeState = useCallback(
    ({
      isLoggedIn,
      credentials,
    }: AuthSource): ScopeStateMap['authentication'] | null => {
      if (!credentials || !isLoggedIn) {
        return { tokens: [] };
      }
      return {
        tokens: [
          {
            issuer: GEOWEB_AUTH_ISSUER,
            token: credentials.token,
            refreshToken: credentials.refresh_token ?? '',
            roles: (credentials.roles ?? []).map((role) => role.name),
            ...(credentials.expires_at
              ? { tokenExpiresAt: credentials.expires_at * 1000 }
              : { tokenExpiresAt: 0 }),
          },
        ],
      };
    },
    [],
  );

  // Return true when a Redux event already matches the bridge state (plugin echo), preventing plugin → Redux → plugin loops without blocking real external updates.
  const isTimeEventRoundTrip = useCallback<IsEventRoundTrip<'time'>>(
    (event: ScopeEventMap['time'], bridgeState: TimeState | undefined) =>
      bridgeState?.time === event.time,
    [],
  );

  const isAnimationEventRoundTrip = useCallback<IsEventRoundTrip<'animation'>>(
    (
      event: ScopeEventMap['animation'],
      bridgeState: AnimationState | undefined,
    ) => {
      if (!bridgeState) {
        return false;
      }
      switch (event.type) {
        case 'setAnimationStartTime':
          return bridgeState.animationStartTime === event.time;
        case 'setAnimationEndTime':
          return bridgeState.animationEndTime === event.time;
        case 'setAnimationTimeSpan':
          return (
            bridgeState.animationStartTime === event.startTime &&
            bridgeState.animationEndTime === event.endTime
          );
        default:
          return false;
      }
    },
    [],
  );

  const toRuntimeEvent = useCallback(
    (
      visibility: PluginVisibility | undefined,
    ): ScopeEventMap['runtime'] | null =>
      visibility
        ? { type: 'setPluginVisibility', pluginVisibility: visibility }
        : null,
    [],
  );

  // The plugin's own setPluginVisibility already moved the bridge state, so
  // mirroring it straight back would be a wasted broadcast.
  const isRuntimeEventRoundTrip = useCallback<IsEventRoundTrip<'runtime'>>(
    (event, bridgeState) =>
      bridgeState?.pluginVisibility === event.pluginVisibility,
    [],
  );

  const isFeatureHoverEventRoundTrip = useCallback<
    IsEventRoundTrip<'featureHover'>
  >((event, bridgeState) => {
    const hoveredFeatureIds =
      bridgeState?.featureLayers.find(
        (layer) => layer.id === event.featureLayerId,
      )?.hoveredFeatureIds ?? [];

    return isEqual(hoveredFeatureIds, event.hoveredFeatureIds);
  }, []);

  useSendOnReduxChange(
    pluginBridge,
    'time',
    timeValue,
    toTimeEvent,
    hasScope('time'),
    isTimeEventRoundTrip,
  );
  useSendOnReduxChange(
    pluginBridge,
    'animation',
    animationState,
    toAnimationEvent,
    hasScope('animation'),
    isAnimationEventRoundTrip,
  );
  useSendOnReduxChange(
    pluginBridge,
    'features',
    pluginFeatureLayers,
    toFeaturesEvents,
    hasScope('features'),
  );
  useSetScopeStateOnChange(
    pluginBridge,
    'featureSelection',
    featureLayerSelections,
    toFeatureSelectionState,
    hasScope('featureSelection'),
  );
  useSendOnReduxChange(
    pluginBridge,
    'featureHover',
    featureLayerHover,
    toFeatureHoverEvents,
    hasScope('featureHover'),
    isFeatureHoverEventRoundTrip,
  );
  useSetScopeStateOnChange(
    pluginBridge,
    'applicationConfiguration',
    applicationConfig,
    toApplicationConfigurationState,
    hasScope('applicationConfiguration'),
  );
  useSetScopeStateOnChange(
    pluginBridge,
    'user',
    authSource,
    toUserScopeState,
    hasScope('user'),
  );
  useSetScopeStateOnChange(
    pluginBridge,
    'authentication',
    authSource,
    toAuthenticationScopeState,
    hasScope('authentication'),
  );
  useSendOnReduxChange(
    pluginBridge,
    'runtime',
    overlayVisibility,
    toRuntimeEvent,
    hasScope('runtime'),
    isRuntimeEventRoundTrip,
  );
};
